import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { BarChart3, Users, ArrowUpDown, DollarSign, TrendingUp, Activity } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/admin/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch reports:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-muted-foreground">読み込み中...</div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-muted-foreground">{t('admin_reports_fetch_failed')}</div>
      </AdminLayout>
    );
  }

  const maxVolume = Math.max(...data.daily_chart.map(d => d.volume), 1);

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-reports-page">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('admin_reports_title')}</h1>
          <p className="text-muted-foreground">{t('admin_reports_subtitle')}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={ArrowUpDown} label={t('admin_reports_total_transfers')} value={data.transfers.total} sub={`${t('admin_reports_this_month')}: ${data.transfers.monthly}${t('count_items')}`} color="orange" />
          <StatCard icon={DollarSign} label={t('admin_reports_total_volume')} value={`¥${(data.volume.total || 0).toLocaleString()}`} sub={`${t('admin_reports_this_month')}: ¥${(data.volume.monthly || 0).toLocaleString()}`} color="emerald" />
          <StatCard icon={TrendingUp} label={t('admin_reports_fee_revenue')} value={`¥${(data.fees.total || 0).toLocaleString()}`} sub={`${t('admin_reports_this_month')}: ¥${(data.fees.monthly || 0).toLocaleString()}`} color="sky" />
          <StatCard icon={Users} label={t('admin_reports_total_users')} value={data.users.total} sub={`${t('admin_reports_kyc_approved')}: ${data.users.kyc_approved}${t('count_people')}`} color="violet" />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Volume Chart */}
          <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="daily-chart">
            <h3 className="font-heading text-lg font-semibold text-primary mb-1">{t('admin_reports_daily_chart')}</h3>
            <p className="text-sm text-muted-foreground mb-6">{t('admin_reports_daily_chart_desc')}</p>
            <div className="flex items-end gap-2 h-48">
              {data.daily_chart.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {day.count > 0 ? `${day.count}件` : ''}
                  </div>
                  <div
                    className="w-full rounded-t-md bg-accent/80 hover:bg-accent transition-colors min-h-[4px]"
                    style={{ height: `${Math.max((day.volume / maxVolume) * 160, 4)}px` }}
                    title={`¥${day.volume.toLocaleString()} (${day.count}件)`}
                  />
                  <div className="text-xs text-muted-foreground">{day.date}</div>
                </div>
              ))}
            </div>
          </div>

          {/* User Stats */}
          <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="user-stats">
            <h3 className="font-heading text-lg font-semibold text-primary mb-1">{t('admin_reports_user_stats')}</h3>
            <p className="text-sm text-muted-foreground mb-6">{t('admin_reports_user_stats_desc')}</p>
            <div className="space-y-4">
              <ProgressBar label={t('admin_reports_active_users')} value={data.users.active} max={data.users.total} color="bg-emerald-500" />
              <ProgressBar label={t('admin_reports_kyc_approved_users')} value={data.users.kyc_approved} max={data.users.total} color="bg-sky-500" />
              <ProgressBar label={t('admin_reports_new_users')} value={data.users.new_this_month} max={data.users.total} color="bg-violet-500" />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-border">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{data.transfers.daily}</div>
                <div className="text-xs text-muted-foreground">{t('admin_reports_today_transfers')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{data.transfers.weekly}</div>
                <div className="text-xs text-muted-foreground">{t('admin_reports_week_transfers')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="top-users">
          <h3 className="font-heading text-lg font-semibold text-primary mb-1">{t('admin_reports_top_users')}</h3>
          <p className="text-sm text-muted-foreground mb-6">{t('admin_reports_top_users_desc')}</p>
          {data.top_users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('admin_reports_no_data')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">{t('admin_reports_rank')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">{t('user')}</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">{t('admin_reports_count')}</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">{t('admin_reports_volume')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_users.map((user, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-3 text-sm font-bold text-accent">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-primary">{user.full_name}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-mono">{user.transfer_count}{t('count_items')}</td>
                      <td className="px-4 py-3 text-right text-sm font-mono font-semibold text-primary">¥{user.total_volume.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  const colorMap = {
    orange: 'bg-orange-500/10 text-orange-600',
    emerald: 'bg-emerald-500/10 text-emerald-600',
    sky: 'bg-sky-500/10 text-sky-600',
    violet: 'bg-violet-500/10 text-violet-600',
  };
  return (
    <div className="bg-card border border-border/50 rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorMap[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div className="text-2xl font-bold text-primary font-mono">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function ProgressBar({ label, value, max, color }) {
  const percent = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-medium text-primary">{value} / {max}</span>
      </div>
      <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
